import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import adminService from '../../services/adminService'
import PageTitle from '../../components/Common/PageTitle'
import Skeleton from 'react-loading-skeleton'
import { toast } from 'react-toastify'
import { toCsv, downloadCsv } from '../../utils/csv'
import UserAvatar from './UserAvatar'

const AdminUserManagement = () => {
  const navigate = useNavigate()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({})
  const [filters, setFilters] = useState({
    search: '',
    tier: '',
    status: '',
    page: 1
  })
  const [selectedUsers, setSelectedUsers] = useState([])
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [subscriptionPlans, setSubscriptionPlans] = useState({})
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null)
  const [detailUserId, setDetailUserId] = useState(null)
  // Per-plan usage caps configured on the Admin Settings page. Drives the
  // "Usage Limit" field in the edit modal so it auto-fills when the admin
  // changes the subscription tier.
  const [tierUsageLimits, setTierUsageLimits] = useState({})

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true)
      const response = await adminService.getUsers({
        page: filters.page,
        limit: 20,
        search: filters.search,
        tier: filters.tier,
        status: filters.status
      })
      
      setUsers(response.users)
      setPagination(response)
    } catch (error) {
      console.error('Error loading users:', error)
      toast.error('Failed to load users')
    } finally {
      setLoading(false)
    }
  }, [filters])

  const loadSubscriptionPlans = useCallback(async () => {
    try {
      const plans = await adminService.getSubscriptionPlans()
      setSubscriptionPlans(plans)
    } catch (error) {
      console.error('Error loading subscription plans:', error)
    }
  }, [])

  const loadTierUsageLimits = useCallback(async () => {
    try {
      const { values } = await adminService.getSystemSettings()
      setTierUsageLimits(values?.maxUsersPerPlan || {})
    } catch (error) {
      // Non-fatal: the modal falls back to keeping whatever value is in the
      // form when the per-tier cap isn't configured.
      console.warn('Failed to load tier usage limits:', error?.message || error)
    }
  }, [])

  useEffect(() => {
    // Check if user is admin
    if (!adminService.isAdmin()) {
      toast.error('Access denied. Admin privileges required.')
      navigate('/dashboards')
      return
    }

    loadUsers()
    loadSubscriptionPlans()
    loadTierUsageLimits()
  }, [navigate, loadUsers, loadSubscriptionPlans, loadTierUsageLimits])

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value // Reset to page 1 when other filters change
    }))
  }

  const handleUserEdit = (user) => {
    setEditingUser(user)
    setShowEditModal(true)
  }

  const handleUserUpdate = async (updatedData) => {
    try {
      await adminService.updateUserSubscription(editingUser.id, updatedData)
      toast.success('User updated successfully')
      setShowEditModal(false)
      setEditingUser(null)
      loadUsers() // Refresh the list
    } catch (error) {
      console.error('Error updating user:', error)
      toast.error('Failed to update user')
    }
  }

  const handleUserDelete = (user) => {
    // Surfaces a typed-confirmation modal instead of a fragile window.confirm,
    // because deleting a user cascades to invoices/payments/usage_logs.
    setConfirmDeleteUser(user)
  }

  const performUserDelete = async (userId) => {
    try {
      await adminService.deleteUser(userId)
      toast.success('User deleted successfully')
      setConfirmDeleteUser(null)
      loadUsers()
    } catch (error) {
      console.error('Error deleting user:', error)
      toast.error('Failed to delete user')
    }
  }

  const handleBulkUpdate = async (action, data) => {
    if (selectedUsers.length === 0) {
      toast.error('Please select users first')
      return
    }

    try {
      if (action === 'updateTier') {
        await adminService.bulkUpdateUserTier(selectedUsers, data.tier)
        toast.success(`Updated ${selectedUsers.length} users to ${data.tier} tier`)
      }
      
      setSelectedUsers([])
      loadUsers()
    } catch (error) {
      console.error('Error in bulk update:', error)
      toast.error('Failed to perform bulk update')
    }
  }

  const handleExportUsers = async () => {
    try {
      const usersData = await adminService.exportUsersData()

      const headers = ['Name', 'Email', 'Subscription Tier', 'Status', 'Usage Count', 'Usage Limit', 'Created At', 'Last Login']
      const rows = usersData.map(user => [
        user.name,
        user.email,
        user.subscription_tier,
        user.subscription_status,
        user.usage_count,
        user.usage_limit,
        new Date(user.created_at).toLocaleDateString(),
        user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'
      ])

      downloadCsv(
        `users-export-${new Date().toISOString().split('T')[0]}.csv`,
        toCsv(headers, rows)
      )

      toast.success('Users data exported successfully')
    } catch (error) {
      console.error('Error exporting users:', error)
      toast.error('Failed to export users data')
    }
  }

  if (loading && users.length === 0) {
    return (
      <div className="p-4">
        <PageTitle title="User Management" />
        <div className="bg-white rounded-lg shadow-sm border mt-6">
          <div className="p-6">
            {Array(5).fill(0).map((_, i) => (
              <div key={i} className="flex items-center justify-between py-4 border-b border-gray-200 last:border-b-0">
                <div className="flex items-center space-x-4">
                  <Skeleton circle width={40} height={40} />
                  <div>
                    <Skeleton width={200} height={20} className="mb-1" />
                    <Skeleton width={150} height={16} />
                  </div>
                </div>
                <Skeleton width={100} height={32} />
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <PageTitle title="User Management" />
        <div className="flex space-x-2">
          <button
            onClick={handleExportUsers}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm"
          >
            Export Users
          </button>
          <button
            onClick={loadUsers}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Tier</label>
            <select
              value={filters.tier}
              onChange={(e) => handleFilterChange('tier', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Tiers</option>
              <option value="free">Free</option>
              <option value="pro">Pro</option>
              <option value="business">Business</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ search: '', tier: '', status: '', page: 1 })}
              className="w-full p-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedUsers.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <span className="text-sm text-blue-700">
              {selectedUsers.length} users selected
            </span>
            <div className="flex space-x-2">
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    handleBulkUpdate('updateTier', { tier: e.target.value })
                    e.target.value = ''
                  }
                }}
                className="text-sm p-1 border border-blue-300 rounded"
                defaultValue=""
              >
                <option value="">Bulk Update Tier</option>
                <option value="free">Free</option>
                <option value="pro">Pro</option>
                <option value="business">Business</option>
              </select>
              <button
                onClick={() => setSelectedUsers([])}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={selectedUsers.length === users.length && users.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedUsers(users.map(u => u.id))
                      } else {
                        setSelectedUsers([])
                      }
                    }}
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className={selectedUsers.includes(user.id) ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUsers(prev => [...prev, user.id])
                        } else {
                          setSelectedUsers(prev => prev.filter(id => id !== user.id))
                        }
                      }}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="mr-4">
                        <UserAvatar
                          name={user.name}
                          email={user.email}
                          avatarUrl={user.avatar_url}
                          className="h-10 w-10"
                          textClassName="text-sm"
                        />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                        {user.phone_number && (
                          <div className="text-xs text-gray-400 flex items-center">
                            {user.phone_number}
                            {user.phone_verified && (
                              <span className="ml-1 text-green-500">✓</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.subscription_tier === 'free' 
                          ? 'bg-gray-100 text-gray-800'
                          : user.subscription_tier === 'pro'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-emerald-100 text-emerald-800'
                      }`}>
                        {user.subscription_tier}
                      </span>
                      {user.role === 'admin' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          Admin
                        </span>
                      )}
                    </div>
                    {user.subscription_end_date && (
                      <div className="text-xs text-gray-500 mt-1">
                        Expires: {new Date(user.subscription_end_date).toLocaleDateString()}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.usage_count}/{user.usage_limit === -1 ? '∞' : user.usage_limit}
                    {user.usage_limit > 0 && (
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                        <div 
                          className="bg-blue-600 h-1.5 rounded-full" 
                          style={{ 
                            width: user.usage_limit === -1 ? '0%' : `${Math.min((user.usage_count / user.usage_limit) * 100, 100)}%` 
                          }}
                        ></div>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.subscription_status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : user.subscription_status === 'cancelled'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {user.subscription_status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>{new Date(user.created_at).toLocaleDateString()}</div>
                    {user.last_login && (
                      <div className="text-xs text-gray-400">
                        Last: {new Date(user.last_login).toLocaleDateString()}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => setDetailUserId(user.id)}
                      className="text-gray-700 hover:text-gray-900 mr-3"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleUserEdit(user)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleUserDelete(user)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                disabled={!pagination.hasPrevPage}
                onClick={() => handleFilterChange('page', filters.page - 1)}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                disabled={!pagination.hasNextPage}
                onClick={() => handleFilterChange('page', filters.page + 1)}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{((filters.page - 1) * 20) + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(filters.page * 20, pagination.totalCount)}
                  </span>{' '}
                  of <span className="font-medium">{pagination.totalCount}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    disabled={!pagination.hasPrevPage}
                    onClick={() => handleFilterChange('page', filters.page - 1)}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    ‹
                  </button>
                  
                  {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                    .filter(page => 
                      page === 1 || 
                      page === pagination.totalPages || 
                      Math.abs(page - filters.page) <= 2
                    )
                    .map(page => (
                      <button
                        key={page}
                        onClick={() => handleFilterChange('page', page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === filters.page
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}

                  <button
                    disabled={!pagination.hasNextPage}
                    onClick={() => handleFilterChange('page', filters.page + 1)}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    ›
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && editingUser && (
        <UserEditModal
          user={editingUser}
          subscriptionPlans={subscriptionPlans}
          tierUsageLimits={tierUsageLimits}
          onClose={() => {
            setShowEditModal(false)
            setEditingUser(null)
          }}
          onSave={handleUserUpdate}
        />
      )}

      {/* Delete typed-confirmation */}
      {confirmDeleteUser && (
        <DeleteConfirmModal
          user={confirmDeleteUser}
          onClose={() => setConfirmDeleteUser(null)}
          onConfirm={() => performUserDelete(confirmDeleteUser.id)}
        />
      )}

      {/* User detail drawer */}
      {detailUserId && (
        <UserDetailDrawer
          userId={detailUserId}
          onClose={() => setDetailUserId(null)}
        />
      )}
    </div>
  )
}

// Top-level so its useState survives parent re-renders. When this was
// declared inside AdminUserManagement, every parent re-render (filter typing,
// cloud-sync tick, etc.) created a brand-new component type, causing the
// modal to remount and its formData to reset to the user's original values.
const UserEditModal = ({ user, subscriptionPlans = {}, tierUsageLimits = {}, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    tier: user?.subscription_tier || 'free',
    status: user?.subscription_status || 'active',
    role: user?.role || 'user',
    endDate: user?.subscription_end_date || '',
    usageLimit: user?.usage_limit ?? 10
  })

  // When the admin picks a different tier, auto-update Usage Limit to the cap
  // configured on the Settings page for that tier. If the cap isn't defined
  // for the new tier, leave the existing value alone so we don't blank it.
  const handleTierChange = (nextTier) => {
    setFormData(prev => {
      const cap = tierUsageLimits?.[nextTier]
      return {
        ...prev,
        tier: nextTier,
        usageLimit: cap !== undefined && cap !== null ? cap : prev.usageLimit
      }
    })
  }

  const tierCap = tierUsageLimits?.[formData.tier]
  const usageLimitMatchesCap =
    tierCap !== undefined && tierCap !== null && Number(formData.usageLimit) === Number(tierCap)

  const handleSubmit = (e) => {
    e.preventDefault()
    const wasAdmin = user?.role === 'admin'
    const becomingAdmin = formData.role === 'admin' && !wasAdmin
    if (becomingAdmin) {
      const typed = window.prompt(
        `You are about to grant ADMIN access to ${user.email}. Admins can manage all users, plans, and revenue.\n\nType ADMIN to confirm.`
      )
      if (typed !== 'ADMIN') {
        toast.info('Admin promotion cancelled')
        return
      }
    }
    onSave(formData)
  }

  if (!user) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Edit User: {user.name}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Tier</label>
            <select
              value={formData.tier}
              onChange={(e) => handleTierChange(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              {Object.keys(subscriptionPlans).map(planKey => (
                <option key={planKey} value={planKey}>
                  {subscriptionPlans[planKey].name}
                </option>
              ))}
            </select>
            {tierCap !== undefined && tierCap !== null && (
              <p className="text-xs text-gray-500 mt-1">
                Tier cap from Settings: {tierCap === -1 ? 'Unlimited' : tierCap}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">User Role</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="user">Regular User</option>
              <option value="admin">Administrator</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Administrators have access to admin panel</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">Usage Limit</label>
              {tierCap !== undefined && tierCap !== null && !usageLimitMatchesCap && (
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, usageLimit: tierCap }))}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Reset to tier cap
                </button>
              )}
            </div>
            <input
              type="number"
              value={formData.usageLimit}
              onChange={(e) => {
                const v = e.target.value
                setFormData(prev => ({ ...prev, usageLimit: v === '' ? '' : parseInt(v, 10) }))
              }}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              min="-1"
              placeholder="-1 for unlimited"
            />
            <p className="text-xs text-gray-500 mt-1">
              {usageLimitMatchesCap
                ? 'Auto-set from tier cap. Override if you want a per-user exception.'
                : 'Use -1 for unlimited. Tier defaults come from Settings → Per-plan user caps.'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subscription End Date</label>
            <input
              type="date"
              value={formData.endDate ? String(formData.endDate).split('T')[0] : ''}
              onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const DeleteConfirmModal = ({ user, onClose, onConfirm }) => {
  const [typed, setTyped] = useState('')
  const expected = user.email
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete user</h3>
        <p className="text-sm text-gray-600 mb-3">
          This will permanently remove <span className="font-medium">{user.name}</span> ({user.email}) and
          cascade-delete their invoices, payments, and usage logs. This cannot be undone.
        </p>
        <p className="text-xs text-gray-500 mb-3">
          To confirm, type the user's email below:
        </p>
        <input
          autoFocus
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={expected}
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500 text-sm mb-4"
        />
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            disabled={typed !== expected}
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Delete user
          </button>
        </div>
      </div>
    </div>
  )
}

const UserDetailDrawer = ({ userId, onClose }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        const result = await adminService.getUserDetails(userId)
        if (!cancelled) setData(result)
      } catch (e) {
        toast.error('Failed to load user details')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [userId])

  return (
    <div className="fixed inset-0 z-50 flex">
      <button aria-label="Close" className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-md bg-white h-full overflow-y-auto shadow-xl">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">User details</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800" aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="p-4">
            <Skeleton height={80} className="mb-3" />
            <Skeleton count={5} />
          </div>
        ) : !data ? (
          <div className="p-4 text-sm text-gray-500">No data.</div>
        ) : (
          <div className="p-4 space-y-5 text-sm">
            <div className="flex items-center space-x-3">
              <UserAvatar
                name={data.user.name}
                email={data.user.email}
                avatarUrl={data.user.avatar_url}
                className="h-12 w-12"
                textClassName="text-base"
              />
              <div>
                <div className="font-semibold text-gray-900">{data.user.name}</div>
                <div className="text-gray-500">{data.user.email}</div>
                {data.user.phone_number && (
                  <div className="text-xs text-gray-500">{data.user.phone_number}{data.user.phone_verified ? ' ✓' : ''}</div>
                )}
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-800 mb-1">Subscription</h4>
              <div className="text-gray-600">
                <div>Tier: <span className="font-medium">{data.user.subscription_tier}</span></div>
                <div>Status: <span className="font-medium">{data.user.subscription_status}</span></div>
                <div>Usage: {data.user.usage_count}/{data.user.usage_limit === -1 ? '∞' : data.user.usage_limit}</div>
                {data.user.subscription_end_date && (
                  <div>Expires: {new Date(data.user.subscription_end_date).toLocaleDateString()}</div>
                )}
                <div>Joined: {new Date(data.user.created_at).toLocaleDateString()}</div>
                {data.user.last_login && <div>Last login: {new Date(data.user.last_login).toLocaleDateString()}</div>}
                {data.user.role === 'admin' && (
                  <div className="mt-1 inline-block px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-800">Administrator</div>
                )}
              </div>
            </div>

            <div>
              <h4 className="font-medium text-gray-800 mb-2">Payments ({data.payments.length})</h4>
              {data.payments.length === 0 ? (
                <p className="text-gray-500 text-xs">No payments.</p>
              ) : (
                <ul className="space-y-2">
                  {data.payments.slice(0, 10).map(p => (
                    <li key={p.id} className="flex justify-between border-b border-gray-100 pb-1">
                      <div>
                        <div>{p.plan_id} • {p.billing_cycle}</div>
                        <div className="text-xs text-gray-500">{new Date(p.created_at).toLocaleDateString()}</div>
                      </div>
                      <div className="text-right">
                        <div>₹{Number(p.amount).toLocaleString()}</div>
                        <div className={`text-xs ${p.status === 'completed' ? 'text-green-600' : p.status === 'failed' ? 'text-red-600' : 'text-yellow-600'}`}>{p.status}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <h4 className="font-medium text-gray-800 mb-2">Recent activity ({data.usageLogs.length})</h4>
              {data.usageLogs.length === 0 ? (
                <p className="text-gray-500 text-xs">No activity logged.</p>
              ) : (
                <ul className="space-y-1 text-xs">
                  {data.usageLogs.slice(0, 15).map(log => (
                    <li key={log.id} className="flex justify-between text-gray-600">
                      <span>{log.action.replace(/_/g, ' ')} <span className="text-gray-400">({log.resource_type})</span></span>
                      <span>{new Date(log.created_at).toLocaleString()}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminUserManagement